import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ContentCard,
  DashboardHeader,
  Icons,
} from "../common/DashboardComponents";
import StatusBadge from "../common/StatusBadge";
import Loader from "../common/Loader";
import { fetchGrievanceEscalationMatrix } from "../../redux/actions/grievanceActions1";
import "bootstrap-icons/font/bootstrap-icons.css";

const EscalatedGrievances = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading: grievancesLoading } = useSelector(
    (state) => state.grievances
  );

  const [grievances, setGrievances] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [grievancesPerPage, setGrievancesPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch grievance data
  const fetchGrievanceData = useCallback(async () => {
    try {
      setIsLoading(true);
      const storedUser = localStorage.getItem("user");
      if (!storedUser) {
        console.error("No user found in localStorage");
        setIsLoading(false);
        return;
      }

      const userObj = JSON.parse(storedUser);
      const response = await dispatch(
        fetchGrievanceEscalationMatrix(userObj?.role)
      );

      if (response) {
        setGrievances(response);
      }
    } catch (error) {
      console.error("Error fetching grievances:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchGrievanceData();
  }, [fetchGrievanceData]);

  // Calculate pagination variables
  const indexOfLastGrievance = currentPage * grievancesPerPage;
  const indexOfFirstGrievance = indexOfLastGrievance - grievancesPerPage;
  const currentGrievances = grievances.slice(
    indexOfFirstGrievance,
    indexOfLastGrievance
  );
  const totalPages = Math.ceil(grievances.length / grievancesPerPage);

  // Pagination functions
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Navigate to details page
  const handleViewDetails = (grievance) => {
    navigate("/EscalatedGrievancesDetails", {
      state: {
        id: grievance.GRV_ID,
        esm: grievance.ESM_ID || "N/A",
        centerName: grievance.UNIT_CENTRE_NAME || "N/A",
      },
    });
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // If total pages less than max, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Calculate start and end of middle range
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at the beginning or end
      if (currentPage <= 2) {
        endPage = 3;
      } else if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div className="container-fluid">
      <DashboardHeader title="Escalated Grievance" />

      <ContentCard title="Escalated Grievances List" icon={Icons.Grievances}>
        {isLoading || grievancesLoading ? (
          <Loader message="Loading grievances..." />
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <select
                  className="form-select form-select-sm"
                  value={grievancesPerPage}
                  onChange={(e) => {
                    setGrievancesPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  style={{ width: "auto" }}
                >
                  <option value={5}>5 per page</option>
                  <option value={8}>8 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={15}>15 per page</option>
                </select>
              </div>
              <div className="text-muted small">
                Showing {grievances.length > 0 ? indexOfFirstGrievance + 1 : 0}{" "}
                to {Math.min(indexOfLastGrievance, grievances.length)} of{" "}
                {grievances.length} grievances
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3">ID</th>
                    <th className="py-3">Applicant Name</th>
                    <th className="py-3">Category</th>
                    <th className="py-3">Grievance Details</th>
                    <th className="py-3">Center Name</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentGrievances.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="bi bi-exclamation-circle text-muted fs-4 d-block mb-2"></i>
                        <p className="text-muted mb-0">No grievances found</p>
                      </td>
                    </tr>
                  ) : (
                    currentGrievances.map((grievance) => (
                      <tr key={grievance.GRV_ID} className="align-middle">
                        <td>{grievance.GRV_ID}</td>
                        <td>{grievance.USER_NAME}</td>
                        <td>{grievance.GRV_CAT}</td>
                        <td>
                          {grievance.GRV_DETAILS &&
                          grievance.GRV_DETAILS.length > 60
                            ? `${grievance.GRV_DETAILS.substring(0, 60)}...`
                            : grievance.GRV_DETAILS}
                        </td>
                        <td>{grievance.UNIT_CENTRE_NAME}</td>
                        <td>
                          <StatusBadge
                            status={grievance.STATUS_NAME}
                            isGrant={true}
                          />
                        </td>
                        <td>
                          <button
                            onClick={() => handleViewDetails(grievance)}
                            className="btn btn-sm btn-primary rounded-pill px-3"
                          >
                            <i className="bi bi-eye-fill me-1"></i> View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {grievances.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">
                  Page {currentPage} of {totalPages}
                </div>

                <nav aria-label="Grievance pagination">
                  <ul className="pagination mb-0">
                    {/* First page button */}
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(1)}
                        className="page-link"
                        aria-label="First"
                      >
                        <i className="bi bi-chevron-double-left"></i>
                      </button>
                    </li>

                    {/* Previous button */}
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        className="page-link"
                        aria-label="Previous"
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>

                    {/* Page numbers */}
                    {getPageNumbers().map((pageNum, index) => (
                      <li
                        key={index}
                        className={`page-item ${
                          pageNum === currentPage ? "active" : ""
                        } ${pageNum === "..." ? "disabled" : ""}`}
                      >
                        <button
                          onClick={() =>
                            pageNum !== "..." ? paginate(pageNum) : null
                          }
                          className={`page-link ${
                            pageNum === currentPage
                              ? "bg-primary text-white"
                              : ""
                          }`}
                        >
                          {pageNum}
                        </button>
                      </li>
                    ))}

                    {/* Next button */}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        className="page-link"
                        aria-label="Next"
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>

                    {/* Last page button */}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(totalPages)}
                        className="page-link"
                        aria-label="Last"
                      >
                        <i className="bi bi-chevron-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </ContentCard>
    </div>
  );
};

export default EscalatedGrievances;
